import { useState, useRef } from 'react';
import useFileDownloader, { getModuleLabelForDownloadUrl } from './useFileDownloader';
import DownloadOtpModal from '../components/DownloadOtpModal/DownloadOtpModal';
import { trackActivityApi } from '../services/activityLog';
import { toastMessage } from '../utils/toastMessage';

/**
 * Hook to trigger sheet/CSV/Excel downloads only after OTP verification.
 * OTP is sent to the admin's registered email on each download request.
 *
 * Use requestDownload()             → for API-based downloads (URL + fileName)
 * Use requestDownloadWithCallback() → for client-side blob/CSV exports
 */
const useDownloadWithPassword = () => {
  const { loading, downloadFile } = useFileDownloader();
  const [modalOpen, setModalOpen] = useState(false);
  const pendingRef = useRef(null);

  const openOtpModal = (options) => {
    pendingRef.current = options;
    setModalOpen(true);
  };

  /** Returns true as soon as work is scheduled so OTP modal can close; download/export runs in background. */
  const handleModalSuccess = () => {
    const pending = pendingRef.current;
    if (!pending) return false;

    if (pending.url) {
      const { url, fileName, params } = pending;
      const token = pending;
      const otpContext = pending.otpContext;
      const activityModule = pending.activityModule;
      const actionPurpose = pending.actionPurpose;
      void (async () => {
        const trackMeta = otpContext
          ? {
              otpExport: true,
              bypassMethod: otpContext.bypassMethod || '',
              purpose: otpContext.purpose || actionPurpose,
              phoneLast4: otpContext.phoneLast4,
              activityModule: otpContext.activityModule || activityModule,
            }
          : null;
        try {
          const ok = await downloadFile(url, fileName, params || {}, trackMeta);
          if (!ok) {
            toastMessage('File download failed. Please try again.', 'error', 'fileDownload');
            if (otpContext) {
              const email = localStorage.getItem('vedic_email') || 'Admin';
              const mod =
                otpContext.activityModule ||
                activityModule ||
                getModuleLabelForDownloadUrl(url, fileName);
              trackActivityApi({
                action: 'EXPORT',
                module: mod,
                description: otpContext.bypassMethod
                  ? `${email} — password-approved export file download failed: ${mod} (purpose: ${otpContext.purpose || actionPurpose})`
                  : `${email} — OTP export file download failed: ${mod} (purpose: ${otpContext.purpose || actionPurpose}, ***${otpContext.phoneLast4})`,
                resourceId: '',
                resourceType: '',
                status: 'failed',
                responseStatus: 400,
                metadata: {
                  otpProtectedExport: true,
                  bypassMethod: otpContext.bypassMethod || '',
                  exportOutcome: 'download_failed',
                  purpose: otpContext.purpose,
                  phoneLast4: otpContext.phoneLast4,
                  activityModule: otpContext.activityModule || mod,
                  responseMessage: 'File download failed',
                },
              }).catch(() => {});
            }
          }
        } finally {
          if (pendingRef.current === token) pendingRef.current = null;
        }
      })();
      return true;
    }

    if (typeof pending.callback === 'function') {
      const { callback, module } = pending;
      const token = pending;
      void (async () => {
        try {
          await Promise.resolve(callback());
          try {
            const email = localStorage.getItem('vedic_email') || 'Admin';
            const mod = module || 'Admin Panel';
            trackActivityApi({
              action: 'EXPORT',
              module: mod,
              description: `${email} downloaded ${mod} sheet`,
              resourceId: '',
              resourceType: '',
            }).catch(() => {});
          } catch {
            /* non-fatal */
          }
        } catch {
          toastMessage('Export failed. Please try again.', 'error', 'fileDownload');
        } finally {
          if (pendingRef.current === token) pendingRef.current = null;
        }
      })();
      return true;
    }

    return false;
  };

  /**
   * Trigger an OTP-protected API-based download.
   * @param {string} url      - API endpoint that returns the file blob
   * @param {string} fileName - Suggested file name for the download
   * @param {object} params   - Optional query params for the API call
   */
  const requestDownload = (url, fileName, params = {}, activityModule = '') => {
    const options = {
      url,
      fileName,
      params,
      actionPurpose: 'Sheet Download',
      activityModule: activityModule || getModuleLabelForDownloadUrl(url, fileName),
    };
    openOtpModal(options);
  };

  /**
   * Trigger an OTP-protected client-side download (blob/CSV generation).
   * @param {Function} callback - Function that performs the actual download
   * @param {string}   module   - Module name for activity log
   */
  const requestDownloadWithCallback = (callback, module = '', actionPurpose = 'Sheet Download') => {
    const options = {
      callback,
      module,
      actionPurpose,
      activityModule: module || 'Admin Panel',
    };
    openOtpModal(options);
  };

  const requestOtpProtectedAction = (
    callback,
    actionPurpose = 'Sensitive Action',
    module = 'Admin Panel',
    otpPurpose = ''
  ) => {
    const options = {
      callback,
      module,
      actionPurpose,
      activityModule: module,
      otpPurpose: otpPurpose || actionPurpose,
    };
    openOtpModal(options);
  };

  const stampOtpContext = (ctx) => {
    if (pendingRef.current) pendingRef.current.otpContext = ctx;
  };

  const DownloadPasswordModal = () => (
    <DownloadOtpModal
      open={modalOpen}
      onClose={() => {
        setModalOpen(false);
        pendingRef.current = null;
      }}
      onSuccess={handleModalSuccess}
      onOtpVerified={stampOtpContext}
      actionPurpose={pendingRef.current?.actionPurpose || 'Sheet Download'}
      otpPurpose={pendingRef.current?.otpPurpose || pendingRef.current?.actionPurpose || 'Sheet Download'}
      activityModule={pendingRef.current?.activityModule || ''}
    />
  );

  return {
    loading,
    downloadFile,
    requestDownload,
    requestDownloadWithCallback,
    requestOtpProtectedAction,
    DownloadPasswordModal,
    openPasswordModal: openOtpModal,
  };
};

export default useDownloadWithPassword;
